import React, { useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import toast from 'react-hot-toast';
import {
  createLogicDefinition, createNamedValue, NamedValueDeclaration, previewLogicExpression, PricingRequestError,
  saveLogicDefinitionVersion,
} from '../../../../services/requests/pricing/logic-definitions-requests';
import {
  Axis, axisProblems, EDITOR_HINTS, EditorKind, FACTS, Fee, feeProblems, LimitSide, LIQUID_CUSTOMER_OWNER, LogicExpression,
  matrixToExpression, MatrixModel, namedValueToExpression, NamedValueModel, NamedValueType, parseFee, PURPOSE_BY_KIND,
  purgeToExpression, PurgeModel, recognize,
} from './conditional-charge-expressions';
import './conditional-charges.css';

export interface LogicDefinition {
  id: number;
  lineageId: string;
  version: number;
  name: string;
  effectiveFrom: string;
  effectiveTo: string;
  status: 'Active' | 'Superseded' | 'Retired';
  expression: LogicExpression;
  editorHint?: string | null;
  createdBy: string;
  createdAt: string;
}

interface DraftAxis { limits: string[]; sides: LimitSide[] }

interface Draft {
  name: string;
  effectiveFrom: string;
  effectiveTo: string;
  description: string;
  purge: { axis: DraftAxis; fees: string[] };
  matrix: { pressure: DraftAxis; temperature: DraftAxis; fees: string[][] };
  value: { name: string; value: string };
}

const KIND_LABEL: Record<EditorKind, string> = { purge: 'Purge Fee', matrix: 'Depressurization Fee', value: 'Named Value' };
const NAMED_VALUE_LIST_ID = 'conditional-charge-named-values';

const text = (value: number | null) => (value === null ? '' : String(value));
/** Fees show cents (8278.50) unless they carry more precision; references show as @name. */
const feeText = (fee: Fee) => {
  if (fee === null) return '';
  if (typeof fee === 'object') return `@${fee.name}`;
  return Math.abs(Math.round(fee * 100) - fee * 100) < 1e-6 ? fee.toFixed(2) : String(fee);
};
const toNumber = (value: string): number | null => (value.trim() === '' ? null : Number(value));
const dateOnly = (value?: string) => (value ?? '').substring(0, 10);
export const formatCurrency = (value: number) => value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fromAxis = (axis: Axis): DraftAxis => ({ limits: axis.limits.map(text), sides: [...axis.sides] });

const newDraft = (kind: EditorKind): Draft => {
  const year = new Date().getFullYear();
  return {
    name: kind === 'value' ? '' : `${KIND_LABEL[kind]} ${year}`,
    effectiveFrom: `${year}-01-01`,
    effectiveTo: `${year}-12-31`,
    description: KIND_LABEL[kind],
    purge: { axis: { limits: [''], sides: ['above'] }, fees: ['', ''] },
    matrix: {
      pressure: { limits: [''], sides: ['below'] },
      temperature: { limits: [''], sides: ['below'] },
      fees: [['', ''], ['', '']],
    },
    value: { name: '', value: '' },
  };
};

const draftFrom = (definition: LogicDefinition, kind: EditorKind, purge?: PurgeModel, matrix?: MatrixModel, value?: NamedValueModel): Draft => {
  const draft = newDraft(kind);
  draft.name = definition.name;
  draft.effectiveFrom = dateOnly(definition.effectiveFrom);
  draft.effectiveTo = dateOnly(definition.effectiveTo);
  if (purge) {
    draft.description = purge.description;
    draft.purge = { axis: fromAxis(purge.axis), fees: purge.fees.map(feeText) };
  }
  if (matrix) {
    draft.description = matrix.description;
    draft.matrix = {
      pressure: fromAxis(matrix.pressure),
      temperature: fromAxis(matrix.temperature),
      fees: matrix.fees.map(row => row.map(feeText)),
    };
  }
  if (value) draft.value = { name: value.name, value: String(value.value) };
  return draft;
};

/** Converts the typed text for a named value of <type> to its stored value, or undefined when it is not valid. */
const parseNamedValue = (type: NamedValueType, raw: string): number | boolean | string | undefined => {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  if (type === 'Number') return Number.isFinite(Number(trimmed)) ? Number(trimmed) : undefined;
  if (type === 'Boolean') return trimmed === 'true' ? true : trimmed === 'false' ? false : undefined;
  if (type === 'Date') return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : undefined;
  return raw;
};

/** Builds the expression from the draft, or lists what must be fixed first. */
const buildExpression = (kind: EditorKind, draft: Draft, namedValues: NamedValueDeclaration[]):
  { expression?: LogicExpression; problems: string[] } => {
  const problems: string[] = [];
  if (!draft.name.trim()) problems.push('Name is required.');
  if (!draft.effectiveFrom || !draft.effectiveTo) problems.push('Effective dates are required.');
  else if (draft.effectiveFrom > draft.effectiveTo) problems.push('Effective to must be on or after effective from.');
  const numberNames = namedValues.filter(v => v.type === 'Number').map(v => v.name);

  if (kind === 'value') {
    const declared = namedValues.find(v => v.name === draft.value.name);
    if (!declared) {
      problems.push('Choose the named value to publish.');
      return { problems };
    }
    const value = parseNamedValue(declared.type, draft.value.value);
    if (value === undefined) problems.push(`Enter a ${declared.type === 'Boolean' ? 'Yes or No' : declared.type.toLowerCase()} value.`);
    if (problems.length) return { problems };
    return { expression: namedValueToExpression({ name: declared.name, type: declared.type, value: value! }), problems };
  }

  if (kind === 'purge') {
    const limits = draft.purge.axis.limits.map(toNumber);
    const fees = draft.purge.fees.map(parseFee);
    problems.push(...axisProblems(limits, 'Contaminant'), ...feeProblems(fees, numberNames));
    if (problems.length) return { problems };
    const model: PurgeModel = { axis: { limits: limits as number[], sides: draft.purge.axis.sides }, fees, description: draft.description };
    return { expression: purgeToExpression(model), problems };
  }

  const pressure = draft.matrix.pressure.limits.map(toNumber);
  const temperature = draft.matrix.temperature.limits.map(toNumber);
  const fees = draft.matrix.fees.map(row => row.map(parseFee));
  problems.push(...axisProblems(pressure, 'Pressure'), ...axisProblems(temperature, 'Temperature'), ...feeProblems(fees.flat(), numberNames));
  if (problems.length) return { problems };
  const model: MatrixModel = {
    pressure: { limits: pressure as number[], sides: draft.matrix.pressure.sides },
    temperature: { limits: temperature as number[], sides: draft.matrix.temperature.sides },
    fees,
    description: draft.description,
  };
  return { expression: matrixToExpression(model), problems };
};

/** Removes range i of an axis: the last range drops its limit; any other range merges into the next one. */
const withoutRange = (axis: DraftAxis, i: number): DraftAxis => {
  const at = i === axis.limits.length ? i - 1 : i;
  return { limits: axis.limits.filter((_, k) => k !== at), sides: axis.sides.filter((_, k) => k !== at) };
};

/** Adds an open-ended range at the end; its limit uses the same side as the previous one. */
const withRangeAdded = (axis: DraftAxis, defaultSide: LimitSide): DraftAxis =>
  ({ limits: [...axis.limits, ''], sides: [...axis.sides, axis.sides[axis.sides.length - 1] ?? defaultSide] });

const withLimit = (axis: DraftAxis, i: number, value: string): DraftAxis =>
  ({ ...axis, limits: axis.limits.map((l, k) => (k === i ? value : l)) });

const withSide = (axis: DraftAxis, i: number, side: LimitSide): DraftAxis =>
  ({ ...axis, sides: axis.sides.map((s, k) => (k === i ? side : s)) });

/** Comparator choice for one limit; both rows next to the limit edit the same choice, so ranges stay contiguous. */
const Comparator = ({ value, readOnly, options, label, onChange }: {
  value: LimitSide; readOnly: boolean; options: Record<LimitSide, string>; label: string; onChange: (side: LimitSide) => void;
}) => (
  <Form.Select size="sm" style={{ width: 52 }} className="px-1" value={value} disabled={readOnly} aria-label={label}
    onChange={e => onChange(e.target.value as LimitSide)}>
    <option value="above">{options.above}</option>
    <option value="below">{options.below}</option>
  </Form.Select>
);

/** "[≥|>] 40 and [<|≤] [limit]": range i with its lower limit (read-only) and its editable upper limit. */
const RangeLimit = ({ axis, index, readOnly, onChange, label }: {
  axis: DraftAxis; index: number; readOnly: boolean; onChange: (axis: DraftAxis) => void; label: string;
}) => {
  const last = index === axis.limits.length;
  const previous = index > 0 ? (axis.limits[index - 1] || '?') : null;
  return (
    <div className="d-flex align-items-center gap-1 flex-nowrap">
      {previous !== null && (
        <>
          <Comparator value={axis.sides[index - 1]} readOnly={readOnly} options={{ above: '≥', below: '>' }}
            label={`${label} range ${index + 1} lower comparator`} onChange={side => onChange(withSide(axis, index - 1, side))} />
          <span className="text-nowrap">{previous}{!last && ' and'}</span>
        </>
      )}
      {!last && (
        <>
          <Comparator value={axis.sides[index]} readOnly={readOnly} options={{ above: '<', below: '≤' }}
            label={`${label} range ${index + 1} upper comparator`} onChange={side => onChange(withSide(axis, index, side))} />
          <Form.Control
            size="sm"
            type="number"
            style={{ width: 76 }}
            value={axis.limits[index]}
            disabled={readOnly}
            aria-label={`${label} limit ${index + 1}`}
            onChange={e => onChange(withLimit(axis, index, e.target.value))}
          />
        </>
      )}
    </div>
  );
};

const FeeInput = ({ value, readOnly, onChange, label }: { value: string; readOnly: boolean; onChange: (v: string) => void; label: string }) => (
  <Form.Control
    size="sm"
    type="text"
    inputMode="decimal"
    list={NAMED_VALUE_LIST_ID}
    className="text-end"
    placeholder="No charge"
    title="An amount, blank for no charge, or @name to use a named value"
    value={value}
    disabled={readOnly}
    aria-label={label}
    onChange={e => onChange(e.target.value)}
  />
);

const RemoveButton = ({ onClick, disabled, label }: { onClick: () => void; disabled: boolean; label: string }) => (
  <Button size="sm" variant="link" className="text-danger p-0 px-1" onClick={onClick} disabled={disabled} aria-label={label} title={label}>
    ✕
  </Button>
);

const PurgeRanges = ({ draft, readOnly, update }: { draft: Draft; readOnly: boolean; update: (d: Draft) => void }) => {
  const { axis, fees } = draft.purge;
  const set = (next: Partial<Draft['purge']>) => update({ ...draft, purge: { ...draft.purge, ...next } });
  return (
    <>
      <Table size="sm" bordered className="align-middle mb-2">
        <thead className="table-light">
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Contaminants before fill (ppm)</th>
            <th style={{ width: 200 }}>Fee (USD)</th>
            {!readOnly && <th style={{ width: 40 }} />}
          </tr>
        </thead>
        <tbody>
          {fees.map((fee, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td>
                <RangeLimit axis={axis} index={i} readOnly={readOnly} label="Contaminant" onChange={next => set({ axis: next })} />
              </td>
              <td>
                <FeeInput value={fee} readOnly={readOnly} label={`Fee for range ${i + 1}`}
                  onChange={v => set({ fees: fees.map((f, k) => (k === i ? v : f)) })} />
              </td>
              {!readOnly && (
                <td className="text-center">
                  <RemoveButton label={`Remove range ${i + 1}`} disabled={fees.length <= 2}
                    onClick={() => set({ axis: withoutRange(axis, i), fees: fees.filter((_, k) => k !== i) })} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
      {!readOnly && (
        <Button size="sm" variant="outline-secondary" onClick={() => set({ axis: withRangeAdded(axis, 'above'), fees: [...fees, ''] })}>
          + Add range
        </Button>
      )}
    </>
  );
};

const PressureMatrix = ({ draft, readOnly, update }: { draft: Draft; readOnly: boolean; update: (d: Draft) => void }) => {
  const { pressure, temperature, fees } = draft.matrix;
  const set = (next: Partial<Draft['matrix']>) => update({ ...draft, matrix: { ...draft.matrix, ...next } });
  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <Table size="sm" bordered className="align-middle mb-2">
          <thead className="table-light">
            <tr>
              <th rowSpan={2} className="text-nowrap">Pressure (psig)</th>
              <th colSpan={temperature.limits.length + 1} className="text-center">Temperature at start of filling (K)</th>
            </tr>
            <tr>
              {fees[0].map((_, c) => (
                <th key={c}>
                  <div className="d-flex align-items-center justify-content-between gap-1">
                    <RangeLimit axis={temperature} index={c} readOnly={readOnly} label="Temperature"
                      onChange={next => set({ temperature: next })} />
                    {!readOnly && (
                      <RemoveButton label={`Remove temperature range ${c + 1}`} disabled={fees[0].length <= 2}
                        onClick={() => set({
                          temperature: withoutRange(temperature, c),
                          fees: fees.map(row => row.filter((_, k) => k !== c)),
                        })} />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fees.map((row, r) => (
              <tr key={r}>
                <th className="table-light">
                  <div className="d-flex align-items-center gap-1">
                    {!readOnly && (
                      <RemoveButton label={`Remove pressure range ${r + 1}`} disabled={fees.length <= 2}
                        onClick={() => set({
                          pressure: withoutRange(pressure, r),
                          fees: fees.filter((_, k) => k !== r),
                        })} />
                    )}
                    <RangeLimit axis={pressure} index={r} readOnly={readOnly} label="Pressure"
                      onChange={next => set({ pressure: next })} />
                  </div>
                </th>
                {row.map((fee, c) => (
                  <td key={c} style={{ minWidth: 110 }}>
                    <FeeInput value={fee} readOnly={readOnly} label={`Fee for pressure range ${r + 1}, temperature range ${c + 1}`}
                      onChange={v => set({ fees: fees.map((cells, k) => (k === r ? cells.map((f, j) => (j === c ? v : f)) : cells)) })} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      {!readOnly && (
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary"
            onClick={() => set({ pressure: withRangeAdded(pressure, 'below'), fees: [...fees, fees[0].map(() => '')] })}>
            + Add pressure range
          </Button>
          <Button size="sm" variant="outline-secondary"
            onClick={() => set({ temperature: withRangeAdded(temperature, 'below'), fees: fees.map(row => [...row, '']) })}>
            + Add temperature range
          </Button>
        </div>
      )}
    </>
  );
};

const TYPE_LABEL: Record<NamedValueType, string> = { Number: 'Number', Boolean: 'Yes / No', String: 'Text', Date: 'Date' };

/** Declares a new named value; it is shared by all customers once created. */
const NewNamedValueForm = ({ onCreated, onCancel }: { onCreated: (d: NamedValueDeclaration) => void; onCancel: () => void }) => {
  const [form, setForm] = useState({ name: '', label: '', type: 'Number', unit: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await createNamedValue({ ...form, unit: form.unit || undefined });
      toast.success(`Named value ${created.name} created.`);
      onCreated(created);
    } catch (e) {
      const err = e as PricingRequestError;
      setError((err.issues ?? []).map(i => i.message).join(' ') || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded p-2 mb-3 bg-light">
      <strong className="d-block mb-2">New named value</strong>
      {error && <Alert variant="danger" className="py-1 px-2">{error}</Alert>}
      <Row className="g-2 align-items-end">
        <Col md={3}>
          <Form.Label htmlFor="nv-name" className="small mb-0">Name</Form.Label>
          <Form.Control id="nv-name" size="sm" placeholder="contractDiscount" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} />
        </Col>
        <Col md={3}>
          <Form.Label htmlFor="nv-label" className="small mb-0">Label</Form.Label>
          <Form.Control id="nv-label" size="sm" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} />
        </Col>
        <Col md={2}>
          <Form.Label htmlFor="nv-type" className="small mb-0">Type</Form.Label>
          <Form.Select id="nv-type" size="sm" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
            {(Object.keys(TYPE_LABEL) as NamedValueType[]).map(t => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label htmlFor="nv-unit" className="small mb-0">Unit</Form.Label>
          <Form.Control id="nv-unit" size="sm" placeholder="USD" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} />
        </Col>
        <Col md={2} className="d-flex gap-1">
          <Button size="sm" onClick={create} disabled={busy}>Create</Button>
          <Button size="sm" variant="outline-secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        </Col>
      </Row>
    </div>
  );
};

/** Publishes one named value with a fixed value for this customer. */
const NamedValueBody = ({ draft, readOnly, update, namedValues, onNamedValueCreated }: {
  draft: Draft; readOnly: boolean; update: (d: Draft) => void;
  namedValues: NamedValueDeclaration[]; onNamedValueCreated: (d: NamedValueDeclaration) => void;
}) => {
  const [creating, setCreating] = useState(false);
  const declared = namedValues.find(v => v.name === draft.value.name);
  const set = (next: Partial<Draft['value']>) => update({ ...draft, value: { ...draft.value, ...next } });
  const choose = (name: string) => {
    const label = namedValues.find(v => v.name === name)?.label;
    update({ ...draft, name: draft.name.trim() || !label ? draft.name : label, value: { name, value: '' } });
  };

  return (
    <>
      {creating && (
        <NewNamedValueForm
          onCancel={() => setCreating(false)}
          onCreated={created => {
            onNamedValueCreated(created);
            setCreating(false);
            update({ ...draft, name: draft.name.trim() ? draft.name : created.label, value: { name: created.name, value: '' } });
          }}
        />
      )}
      <Row className="g-3 align-items-end">
        <Col md={6}>
          <Form.Label htmlFor="nv-select">Named value</Form.Label>
          <div className="d-flex gap-2">
            <Form.Select id="nv-select" value={draft.value.name} disabled={readOnly} onChange={e => choose(e.target.value)}>
              <option value="">-- Choose --</option>
              {namedValues.map(v => (
                <option key={v.name} value={v.name}>{v.label} ({v.name}{v.unit ? `, ${v.unit}` : ''})</option>
              ))}
            </Form.Select>
            {!readOnly && !creating && (
              <Button variant="outline-secondary" className="text-nowrap" onClick={() => setCreating(true)}>+ New</Button>
            )}
          </div>
          {declared?.description && <small className="text-muted">{declared.description}</small>}
        </Col>
        <Col md={4}>
          <Form.Label htmlFor="nv-value">Value{declared?.unit ? ` (${declared.unit})` : ''}</Form.Label>
          {declared?.type === 'Boolean' ? (
            <Form.Select id="nv-value" value={draft.value.value} disabled={readOnly} onChange={e => set({ value: e.target.value })}>
              <option value="">-- Choose --</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Form.Select>
          ) : (
            <Form.Control
              id="nv-value"
              type={declared?.type === 'Number' ? 'number' : declared?.type === 'Date' ? 'date' : 'text'}
              value={draft.value.value}
              disabled={readOnly || !declared}
              onChange={e => set({ value: e.target.value })}
            />
          )}
        </Col>
      </Row>
      <small className="text-muted d-block mt-2">
        Other definitions for this customer can use this value by entering @{draft.value.name || 'name'} instead of an amount.
      </small>
    </>
  );
};

/** Evaluates the current (unsaved) layout against values the user types in. Nothing is stored. */
const TestPanel = ({ kind, draft, namedValues, customerId }: {
  kind: EditorKind; draft: Draft; namedValues: NamedValueDeclaration[]; customerId: number | string;
}) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const facts = kind === 'purge'
    ? [{ key: FACTS.purgePpm, label: 'Contaminants', unit: 'ppm' }]
    : [{ key: FACTS.pressure, label: 'Pressure', unit: 'psig' }, { key: FACTS.initialTemp, label: 'Temperature', unit: 'K' }];

  const run = async () => {
    const { expression, problems } = buildExpression(kind, draft, namedValues);
    if (!expression) {
      setResult(problems.join(' '));
      return;
    }
    setBusy(true);
    try {
      const payload = Object.fromEntries(facts.map(f => [f.key, values[f.key]?.trim() ? values[f.key] : null]));
      // Named values (@name) resolve from this customer's saved values effective on the start date.
      const response = await previewLogicExpression(PURPOSE_BY_KIND[kind], expression, payload,
        { ownerType: LIQUID_CUSTOMER_OWNER, ownerId: String(customerId), asOf: draft.effectiveFrom });
      const lines = (response.outputs ?? []).map((o: any) => `${o.fields.description}: ${formatCurrency(Number(o.fields.quantity) * Number(o.fields.unitPrice))}`);
      if (response.status === 'Matched' || response.status === 'NoMatch') setResult(lines.length ? lines.join(', ') : 'No charge');
      else setResult([...(response.issues ?? []), ...(response.validation?.issues ?? [])].map((i: any) => i.message).join(' ') || response.status);
    } catch (e) {
      setResult((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded p-2 mt-3 bg-light">
      <div className="d-flex flex-wrap align-items-center gap-2">
        <strong>Test a reading:</strong>
        {facts.map(f => (
          <span key={f.key} className="d-flex align-items-center gap-1">
            {f.label}
            <Form.Control size="sm" type="number" style={{ width: 100 }} aria-label={`Test ${f.label}`}
              value={values[f.key] ?? ''} onChange={e => setValues({ ...values, [f.key]: e.target.value })} />
            {f.unit}
          </span>
        ))}
        <Button size="sm" variant="outline-primary" onClick={run} disabled={busy}>
          Test{busy && <Spinner animation="border" size="sm" className="ms-1" />}
        </Button>
        {result && <span data-testid="test-result">→ <strong>{result}</strong></span>}
      </div>
    </div>
  );
};

export interface ConditionalChargeEditorProps {
  show: boolean;
  kind: EditorKind | null;
  definition: LogicDefinition | null;
  readOnly: boolean;
  customerId: number | string;
  namedValues: NamedValueDeclaration[];
  onNamedValueCreated: (declaration: NamedValueDeclaration) => void;
  onClose: () => void;
  onSaved: (saved: LogicDefinition) => void;
}

export const ConditionalChargeEditor = ({
  show, kind: requestedKind, definition, readOnly, customerId, namedValues, onNamedValueCreated, onClose, onSaved,
}: ConditionalChargeEditorProps) => {
  const recognized = useMemo(() => (definition ? recognize(definition.expression, definition.editorHint) : null), [definition]);
  const kind: EditorKind | null = definition ? recognized?.kind ?? null : requestedKind;
  const [draft, setDraft] = useState<Draft>(() => (definition && recognized
    ? draftFrom(definition, recognized.kind, recognized.purge, recognized.matrix, recognized.value)
    : newDraft(requestedKind ?? 'purge')));
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const numberNames = namedValues.filter(v => v.type === 'Number');

  const save = async () => {
    if (!kind) return;
    const { expression, problems } = buildExpression(kind, draft, namedValues);
    if (!expression) {
      setErrors(problems);
      return;
    }
    setErrors([]);
    setSaving(true);
    const request = {
      name: draft.name.trim(),
      effectiveFrom: draft.effectiveFrom,
      effectiveTo: draft.effectiveTo,
      editorHint: EDITOR_HINTS[kind],
      expression,
    };
    try {
      const saved = definition
        ? await saveLogicDefinitionVersion(definition.id, request)
        : await createLogicDefinition({ ...request, purpose: PURPOSE_BY_KIND[kind], ownerType: LIQUID_CUSTOMER_OWNER, ownerId: String(customerId) });
      toast.success(`${draft.name.trim()} saved.`);
      onSaved(saved);
    } catch (e) {
      const error = e as PricingRequestError;
      const details = (error.issues ?? []).filter(i => i.severity === 'Error').map(i => i.message);
      setErrors(details.length ? details : [error.message]);
    } finally {
      setSaving(false);
    }
  };

  const title = kind ? KIND_LABEL[kind] : 'Custom expression';
  const status = definition && definition.status !== 'Active' ? ` (version ${definition.version}, ${definition.status.toLowerCase()})` : '';

  return (
    <Modal show={show} onHide={onClose} size="xl" scrollable backdrop={readOnly ? true : 'static'}
      dialogClassName={kind === 'matrix' ? 'conditional-charge-wide' : undefined}>
      <Modal.Header closeButton>
        <Modal.Title>{title}{status}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errors.length > 0 && (
          <Alert variant="danger" className="py-2">
            {errors.length === 1 ? errors[0] : <ul className="mb-0">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
          </Alert>
        )}
        {!kind && definition && (
          <>
            <Alert variant="secondary" className="py-2">This definition was not created by a pricing editor and can only be viewed.</Alert>
            <pre className="border rounded p-2 bg-light" style={{ maxHeight: 400 }}>{JSON.stringify(definition.expression, null, 2)}</pre>
          </>
        )}
        {kind && (
          <>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Label htmlFor="cc-name">Name</Form.Label>
                <Form.Control id="cc-name" value={draft.name} disabled={readOnly} maxLength={200}
                  onChange={e => setDraft({ ...draft, name: e.target.value })} />
              </Col>
              <Col md={3}>
                <Form.Label htmlFor="cc-from">Effective date</Form.Label>
                <Form.Control id="cc-from" type="date" value={draft.effectiveFrom} disabled={readOnly}
                  onChange={e => setDraft({ ...draft, effectiveFrom: e.target.value })} />
              </Col>
              <Col md={3}>
                <Form.Label htmlFor="cc-to">Expiration date</Form.Label>
                <Form.Control id="cc-to" type="date" value={draft.effectiveTo} disabled={readOnly}
                  onChange={e => setDraft({ ...draft, effectiveTo: e.target.value })} />
              </Col>
            </Row>
            {kind === 'value' && (
              <NamedValueBody draft={draft} readOnly={readOnly} update={setDraft}
                namedValues={namedValues} onNamedValueCreated={onNamedValueCreated} />
            )}
            {kind === 'purge' && <PurgeRanges draft={draft} readOnly={readOnly} update={setDraft} />}
            {kind === 'matrix' && <PressureMatrix draft={draft} readOnly={readOnly} update={setDraft} />}
            {kind !== 'value' && (
              <>
                <datalist id={NAMED_VALUE_LIST_ID}>
                  {numberNames.map(v => <option key={v.name} value={`@${v.name}`}>{v.label}</option>)}
                </datalist>
                {numberNames.length > 0 && !readOnly && (
                  <small className="text-muted d-block mt-2">Fees can use a named value: type @ and choose one, e.g. @{numberNames[0].name}.</small>
                )}
                <TestPanel kind={kind} draft={draft} namedValues={namedValues} customerId={customerId} />
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!readOnly && kind && (
          <small className="text-muted me-auto">Saving makes this the active version immediately; earlier versions stay in History.</small>
        )}
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && kind && (
          <Button variant="primary" onClick={save} disabled={saving}>
            Save{saving && <Spinner animation="border" size="sm" className="ms-1" />}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};